import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, House, Warning, Camera, Wrench, CheckCircle } from "@phosphor-icons/react";
import { api, adminHeaders, getSecret, fileUrl } from "@/lib/api";

const STATUS_LABEL = {
    belum_diproduksi: "Belum Diproduksi",
    sedang_diproduksi: "Sedang Diproduksi",
    finishing_budhi: "Finishing Budhi",
    finishing_cibogo: "Finishing Cibogo",
    siap_kirim: "Siap Kirim",
    terkirim: "Barang Terkirim",
    selesai: "Selesai Install",
};

const STATUS_LINK = {
    sedang_diproduksi: "/admin/produksi",
    finishing_budhi: "/admin/finishing",
    finishing_cibogo: "/admin/finishing",
    siap_kirim: "/admin/siap-kirim",
    terkirim: "/admin/terkirim",
};

const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" });

const fmtDay = (iso) =>
    new Date(iso).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short" });

// Auto-pager: memutar halaman konten secara otomatis (mode display/carousel)
const usePager = (totalPages, ms = 12000) => {
    const [page, setPage] = useState(0);
    useEffect(() => {
        if (totalPages <= 1) {
            setPage(0);
            return;
        }
        const t = setInterval(() => setPage((p) => (p + 1) % totalPages), ms);
        return () => clearInterval(t);
    }, [totalPages, ms]);
    useEffect(() => {
        if (page >= totalPages) setPage(0);
    }, [page, totalPages]);
    return page;
};

const PageDots = ({ page, pages }) =>
    pages > 1 ? (
        <div className="flex gap-1 justify-center mt-2">
            {Array.from({ length: pages }).map((_, i) => (
                <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === page ? "bg-primary" : "bg-border"}`} />
            ))}
        </div>
    ) : null;

export default function OwnerDashboardPage() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [now, setNow] = useState(new Date());

    const load = useCallback(async () => {
        try {
            const res = await api.get("/admin/owner-dashboard", { headers: adminHeaders() });
            setData(res.data);
        } catch {
            /* display tetap menampilkan data terakhir */
        }
    }, []);

    useEffect(() => {
        if (!getSecret()) {
            navigate("/admin");
            return;
        }
        load();
        const t = setInterval(load, 60000);
        const c = setInterval(() => setNow(new Date()), 1000);
        return () => {
            clearInterval(t);
            clearInterval(c);
        };
    }, [load, navigate]);

    const pipeline = (data?.pipeline || []).filter((p) => p.status !== "selesai");
    const warnings = data?.warnings || [];
    const photos = data?.photos || [];
    const statusChanges = data?.status_changes || [];

    const ROLL_VISIBLE = 4;
    const WARN_PER_PAGE = 4;
    const warnPage = usePager(Math.max(1, Math.ceil(warnings.length / WARN_PER_PAGE)));
    const PHOTO_PER_PAGE = 6;
    const photoPage = usePager(Math.max(1, Math.ceil(photos.length / PHOTO_PER_PAGE)));

    return (
        <div className="h-screen bg-background text-foreground font-sans flex flex-col overflow-hidden" data-testid="owner-display">
            <header className="flex-none px-6 py-3 border-b border-stone-200 flex items-center gap-4 bg-white/80 backdrop-blur-xl">
                <button
                    data-testid="owner-back-btn"
                    onClick={() => navigate("/admin/dashboard")}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    title="Kembali"
                >
                    <ArrowLeft size={18} weight="light" />
                </button>
                <img src="/logo.png" alt="INERRE interior" className="h-7 object-contain" />
                <h1 className="font-serif text-xl tracking-tight font-medium" data-testid="owner-title">Dashboard Owner</h1>
                <div className="ml-auto flex items-center gap-4">
                    {data && (
                        <span className="text-xs text-muted-foreground" data-testid="owner-generated-at">
                            Data {fmtTime(data.generated_at)} WIB
                        </span>
                    )}
                    <span className="font-serif text-xl tracking-tight font-medium tabular-nums" data-testid="owner-clock">
                        {now.toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </span>
                    <button
                        data-testid="owner-home-btn"
                        onClick={() => navigate("/admin")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        title="Kembali ke pemindai"
                    >
                        <House size={18} weight="light" />
                    </button>
                </div>
            </header>

            {!data ? (
                <p className="p-6 text-sm text-muted-foreground">Memuat...</p>
            ) : (
                <>
                    <section className="flex-none px-6 pt-4" data-testid="owner-pipeline">
                        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                            {pipeline.map((p) => {
                                const needRoll = p.rooms.length > ROLL_VISIBLE;
                                const rolled = needRoll ? [...p.rooms, ...p.rooms] : p.rooms;
                                return (
                                    <div key={p.status} data-testid={`pipeline-${p.status}`} className="border border-border bg-white p-4 h-60 flex flex-col overflow-hidden">
                                        <p className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground leading-tight flex-none">{STATUS_LABEL[p.status]}</p>
                                        <p className={`font-serif text-5xl tracking-tight font-medium mt-1 flex-none ${p.count > 0 ? "" : "text-muted-foreground/30"}`} data-testid={`pipeline-count-${p.status}`}>
                                            {p.count}
                                        </p>
                                        <div className="h-32 overflow-hidden mt-1.5 flex-none" data-testid={`pipeline-rooms-${p.status}`}>
                                            <div
                                                className={needRoll ? "credit-roll" : ""}
                                                style={needRoll ? { animationDuration: `${Math.max(10, p.rooms.length * 2)}s` } : undefined}
                                            >
                                                {rolled.map((r, i) => (
                                                    <p key={`${r.id}-${i}`} className="text-sm leading-8 truncate">
                                                        {r.property ? <span className="text-muted-foreground">{r.property} · </span> : null}{r.name}
                                                    </p>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    <section className="flex-1 min-h-0 px-6 py-4 grid lg:grid-cols-2 gap-6 overflow-hidden">
                        <div className="min-h-0 flex flex-col" data-testid="owner-warnings">
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3 flex items-center gap-2 flex-none">
                                <Warning size={15} weight="light" /> Warning Aktif ({warnings.length})
                            </p>
                            {warnings.length === 0 ? (
                                <div className="border border-green-300 bg-green-50/40 p-5 flex items-center gap-3 h-40">
                                    <CheckCircle size={22} weight="light" className="text-green-700 shrink-0" />
                                    <p className="text-sm">Tidak ada warning — semua ruangan lengkap.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="grid sm:grid-cols-2 gap-2 content-start">
                                        {Array.from({ length: WARN_PER_PAGE }).map((_, i) => {
                                            const w = warnings[warnPage * WARN_PER_PAGE + i];
                                            return w ? (
                                                <div key={w.room_id} data-testid={`owner-warning-${w.room_id}`} className="border border-red-300 bg-red-50/40 p-4 h-32 overflow-hidden">
                                                    <p className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{w.property}</p>
                                                    <p className="font-serif text-2xl tracking-tight font-medium mt-0.5 truncate">{w.room}</p>
                                                    <p className="text-xs text-red-700 mt-1.5 leading-relaxed line-clamp-2">{w.reasons.join(" • ")}</p>
                                                </div>
                                            ) : (
                                                <div key={`warn-empty-${i}`} className="h-32 border border-transparent" aria-hidden="true" />
                                            );
                                        })}
                                    </div>
                                    <div className="h-4">
                                        <PageDots page={warnPage} pages={Math.ceil(warnings.length / WARN_PER_PAGE)} />
                                    </div>
                                </>
                            )}
                            {statusChanges.length > 0 && (
                                <div className="mt-4" data-testid="owner-status-changes">
                                    <p className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground mb-1.5">Status Berubah Terbaru</p>
                                    <div className="space-y-1">
                                        {statusChanges.slice(0, 4).map((s, i) => (
                                            <p key={i} className="text-xs border border-border bg-white px-3 py-2" data-testid={`owner-status-${i}`}>
                                                {s.message} <span className="text-muted-foreground">· {fmtDay(s.at)} {fmtTime(s.at)}</span>
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="min-h-0 flex flex-col" data-testid="owner-activity">
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3 flex-none">Aktivitas Terbaru</p>
                            {photos.length === 0 && statusChanges.length === 0 ? (
                                <p className="text-sm text-muted-foreground border border-border bg-white p-5">Belum ada aktivitas tercatat.</p>
                            ) : (
                                <>
                                    {photos.length > 0 && (
                                        <div className="grid grid-cols-3 gap-2 content-start" data-testid="owner-photos">
                                            {Array.from({ length: PHOTO_PER_PAGE }).map((_, i) => {
                                                const p = photos[photoPage * PHOTO_PER_PAGE + i];
                                                return p ? (
                                                    <div key={`${p.path}-${i}`} className="border border-border bg-white" data-testid={`owner-photo-${i}`}>
                                                        <img src={fileUrl(p.path)} alt={p.room} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                                                        <div className="p-2 h-12">
                                                            <p className="text-xs font-medium flex items-center gap-1 truncate">
                                                                {p.type === "service" ? <Wrench size={11} weight="light" className="shrink-0" /> : <Camera size={11} weight="light" className="shrink-0" />}
                                                                <span className="truncate">{p.property} — {p.room}</span>
                                                            </p>
                                                            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{fmtDay(p.at)} {fmtTime(p.at)} · {p.by}</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div key={`photo-empty-${i}`} aria-hidden="true">
                                                        <div className="w-full aspect-[4/3] border border-transparent" />
                                                        <div className="h-12" />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <div className="h-4">
                                        <PageDots page={photoPage} pages={Math.ceil(photos.length / PHOTO_PER_PAGE)} />
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
